import { resolveIcon, type IconKey } from '../../icons';
export type AuthHeadersFn = (json?: boolean) => HeadersInit;

export type UnitType = 'fixed_post' | 'vehicle' | 'drone';
export type UnitStatus = 'off_duty' | 'on_duty' | 'enroute' | 'on_scene' | 'unavailable';

export interface FieldUnit {
  id: number;
  name: string;
  callsign?: string | null;
  unit_type: UnitType;
  status: UnitStatus;
  latitude?: number | null;
  longitude?: number | null;
  home_latitude?: number | null;
  home_longitude?: number | null;
  heading?: number | null;
  speed?: number | null;
  battery?: number | null;
  last_fix_at?: string | null;
  description?: string | null;
  icon?: string | null;
  color: string;
  status_color?: string;
  metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface FieldUnitStats {
  total: number;
  by_type: Record<string, number>;
  by_status: Record<string, number>;
}

export interface TrailPoint {
  lat: number;
  lng: number;
  accuracy?: number | null;
  heading?: number | null;
  speed?: number | null;
  battery?: number | null;
  recorded_at: string;
}

export const UNIT_TYPES: { value: UnitType; label: string; icon: IconKey }[] = [
  { value: 'fixed_post', label: 'Guard Post', icon: 'post' },
  { value: 'vehicle', label: 'Patrol Vehicle', icon: 'vehicle' },
  { value: 'drone', label: 'Drone Patrol', icon: 'drone' },
];

/** A video feed carried by a unit, stored in `metadata.stream`. */
export type UnitStream =
  | { type: 'demo' }
  | { type: 'rtmp'; url: string }
  | { type: 'rtsp'; url: string };

/** Read a unit's feed descriptor, or null when it carries none. */
export function unitStream(u: Pick<FieldUnit, 'metadata'>): UnitStream | null {
  const s = u.metadata?.stream as { type?: string; url?: string } | undefined;
  if (!s) return null;
  if (s.type === 'demo') return { type: 'demo' };
  if ((s.type === 'rtmp' || s.type === 'rtsp') && typeof s.url === 'string' && s.url) {
    return { type: s.type, url: s.url };
  }
  return null;
}

export const UNIT_STATUSES: { value: UnitStatus; label: string; color: string }[] = [
  { value: 'off_duty', label: 'Off Duty', color: '#6b7280' },
  { value: 'on_duty', label: 'On Duty', color: '#22c55e' },
  { value: 'enroute', label: 'En Route', color: '#f59e0b' },
  { value: 'on_scene', label: 'On Scene', color: '#ef4444' },
  { value: 'unavailable', label: 'Unavailable', color: '#334155' },
];

export const STATUS_COLOR: Record<UnitStatus, string> = {
  off_duty: '#6b7280',
  on_duty: '#22c55e',
  enroute: '#f59e0b',
  on_scene: '#ef4444',
  unavailable: '#334155',
};

export function unitIcon(u: Pick<FieldUnit, 'unit_type' | 'icon'>): IconKey {
  // u.icon may still hold a legacy emoji written before the icon migration.
  if (u.icon) return resolveIcon(u.icon);
  return UNIT_TYPES.find(t => t.value === u.unit_type)?.icon || 'site';
}
